import React from 'react';
import { ArrowRight, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Group } from '../../context/LeadAttendanceContext';
import LanguageSwitcher from './LanguageSwitcher';

interface GroupSelectionScreenProps {
  groups: Group[];
  leadName?: string;
  onSelectGroup: (groupId: string) => Promise<void>;
  onChangeLeadCode: () => void;
  error?: string | null;
}

export default function GroupSelectionScreen({
  groups,
  leadName,
  onSelectGroup,
  onChangeLeadCode,
  error,
}: GroupSelectionScreenProps) {
  const { t } = useTranslation();
  return (
    <section className="min-h-screen bg-slate-50 px-4 py-6">
      <div className="mx-auto w-full max-w-3xl">
        <header className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-card sm:p-5">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm text-slate-500">{t('leadPrefix')}: {leadName || t('unknownLead')}</p>
              <h2 className="mt-1 text-xl font-semibold text-slate-900">{t('selectActiveGroup')}</h2>
              <p className="mt-1 text-sm text-slate-600">{t('reportingForToday')}</p>
            </div>
            <LanguageSwitcher variant="light" />
          </div>
        </header>

        <div className="grid gap-3 sm:grid-cols-2">
          {groups.map((group) => (
            <button
              key={group.id}
              type="button"
              onClick={() => onSelectGroup(String(group.id))}
              className="flex min-h-[120px] w-full items-start justify-between rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-brand-500 hover:shadow-card"
            >
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                  <Users className="h-3.5 w-3.5" />
                  {t('groupLabel')}
                </span>
                <p className="mt-2 text-base font-semibold text-slate-900">{group.name}</p>
              </div>
              <ArrowRight className="mt-1 h-4 w-4 text-slate-400" />
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={onChangeLeadCode}
          className="mt-4 inline-flex h-11 items-center rounded-xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-100"
        >
          {t('changeLeadCode')}
        </button>

        {error && (
          <p className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}
